import { useState } from "react";
import { useAuth } from "../../../components/context/LoginContext/AuthContext";
import { ERROR_MESSAGES } from "../../../constant/constant";

/**
 * @typedef {Object} AssignQuestData
 * @property {number[]} quest_ids - Array of quest IDs to assign
 * @property {string} remark - Remark/note for the allocation
 */

/**
 * @typedef {Object} AssignQuestSuccessResponse
 * @property {string} message - Success message (e.g., "Quest access updated for Sch 11")
 * @property {number[]} quest_ids - Array of assigned quest IDs
 */

/**
 * @typedef {Object} AssignQuestErrorResponse
 * @property {boolean} success 
 * @property {string} message - Error message
 * @property {string} error_code - Error code (e.g., "MODEL_NOT_FOUND")
 */

/**
 * Custom hook to assign quests to a school/institution
 *
 * @returns {{
 *   assignQuestToSchool: (schoolId: string|number, questData: AssignQuestData, onSuccess?: (message: string) => void) => Promise<void>,
 *   loading: boolean,
 *   error: string,
 *   data: AssignQuestSuccessResponse | null,
 *   setError: React.Dispatch<React.SetStateAction<string>>
 * }}
 */
export const useAssignQuestToSchool = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [data, setData] = useState(null);
    const { fetchWithAuth } = useAuth();
    const apiUrl = process.env.REACT_APP_API_URL;

    /**
     * Assigns quests to a school by updating quest access
     * @param {string|number} schoolId - The ID of the school/institution
     * @param {AssignQuestData} questData - The quest assignment payload
     * @param {(message: string) => void} [onSuccess] - Callback function to be called on success
     */
    const assignQuestToSchool = async (schoolId, questData, onSuccess) => {
        if (!schoolId) {
            setError("School ID is required.");
            return;
        }

        setLoading(true);
        setError("");
        setData(null);

        try {
            const response = await fetchWithAuth(
                `${apiUrl}/school/quest-access/${schoolId}/update`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(questData),
                },
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => null);

                if (response.status === 500) {
                    throw new Error(ERROR_MESSAGES.INTERNAL_SERVER);
                }

                throw new Error(
                    errorData?.message || ERROR_MESSAGES.UNKNOWN,
                );
            }

            const responseData = await response.json();
            setData(responseData);

            if (onSuccess) {
                onSuccess(
                    responseData?.message || "Quests assigned to school successfully",
                );
            }
        } catch (err) {
            setError(err?.message || ERROR_MESSAGES.UNKNOWN);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    return {
        assignQuestToSchool,
        loading,
        error,
        data,
        setError,
    };
};